import * as THREE from 'three';
import { MeshPoolObjects, PackedFrame, ObstacleType } from './types';
import {
  NARROW_LINE_CONFIGS,
  WIDE_LINE_MAT_CONFIGS,
  texturePaths,
  LINE_ICON_MAX,
  lineIconPaths,
  TRAFFIC_LIGHT_ICON_MAX,
  trafficLightIconPaths,
} from './styleConfig';
import TrafficLightLabelPool from './TrafficLightLabelPool';

export default class MeshPool {
  scene: THREE.Scene;
  // 使用 MeshPoolObjects 的部分类型，初始化时逐步填充
  meshPool: MeshPoolObjects = {} as MeshPoolObjects;
  iconPool: Map<ObstacleType, THREE.Points> = new Map(); // 按类型存储图标 Points
  // 起终点图标池，key = 'start' | 'end'
  lineIconPool: Map<string, THREE.Points> = new Map();
  // 红绿灯图标池，key = 'main' | 'grey' | 'pass'
  trafficLightIconPool: Map<string, THREE.Points> = new Map();
  // 红绿灯 id 文本池（封装为独立类，双池分离）
  trafficLightLabelPool!: TrafficLightLabelPool;

  // 每种窄线样式的 LineSegments 池（例如 'solid_white', 'dash_white'）
  narrowLineMeshPool: Map<string, THREE.LineSegments> = new Map();
  // 按样式的宽线 Mesh（routing / planning_path / planning_trajectory）
  wideLineMeshPool: Map<string, THREE.Mesh> = new Map();

  // 帧计数器（用于迟缓回收判断）
  frameCounter: number = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.initSystems();
  }

  /**
   * 初始化mesh
   *
   * @private
   * @memberof MeshPool
   */
  private initSystems() {
    // 1. 线框系统 (开启顶点色)，预分配足够的顶点缓冲
    const maxObsLineVerts = 50000;
    const lineGeom = new THREE.BufferGeometry();
    lineGeom.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(maxObsLineVerts * 3), 3)
    );
    lineGeom.setAttribute(
      'color',
      new THREE.BufferAttribute(new Float32Array(maxObsLineVerts * 3), 3)
    );
    this.meshPool.obsLine = new THREE.LineSegments(
      lineGeom,
      new THREE.LineBasicMaterial({ vertexColors: true })
    );
    // 关闭引擎剔除，直接交由 GPU 渲染管线切除视口外顶点
    this.meshPool.obsLine.frustumCulled = false;

    // 2. 填充面系统 (开启顶点色，材质统一使用 MeshBasicMaterial 以支持透明)
    const maxObsSurfaceVerts = 50000;
    const surfGeom = new THREE.BufferGeometry();
    this.meshPool.obsSurface = new THREE.Mesh(
      surfGeom,
      new THREE.MeshBasicMaterial({
        vertexColors: true, // 使用顶点色区分 19 种类型
        transparent: true,
        opacity: 0.3,
        depthWrite: false, // 避免透明面写入深度导致遮挡/闪烁
        polygonOffset: true,
        polygonOffsetFactor: -1.0,
        polygonOffsetUnits: 1.0,
        side: THREE.DoubleSide,
      })
    );
    // 关闭引擎剔除，直接交由 GPU 渲染管线切除视口外顶点
    this.meshPool.obsSurface.frustumCulled = false;

    // 预分配 position & color 属性
    // 注意：这里需要按照 "元素数量 * 3（xyz 或 rgb）" 来分配缓冲
    surfGeom.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(maxObsSurfaceVerts * 3), 3)
    );
    surfGeom.setAttribute(
      'color',
      new THREE.BufferAttribute(new Float32Array(maxObsSurfaceVerts * 3), 3)
    );

    // 3. 初始化 19 种图标系统
    // 使用 Points + PointsMaterial 来渲染图标（高性能、像素大小可控）
    const maxIconsPerType = 500;
    texturePaths.forEach((path, type) => {
      const tex = path ? new THREE.TextureLoader().load(path) : null;
      const materialOpts: any = {
        size: 32,
        sizeAttenuation: false,
        alphaTest: 0.5,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      };
      if (tex) materialOpts.map = tex;

      const material = new THREE.PointsMaterial(materialOpts);

      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array(maxIconsPerType * 3), 3)
      );
      // 初始不绘制任何点
      geom.setDrawRange(0, 0);

      const points = new THREE.Points(geom, material);
      (points as any).userData = { count: 0, max: maxIconsPerType };
      this.scene.add(points);
      this.iconPool.set(type as ObstacleType, points);
    });

    // 3.1 初始化 起/终点 图标池（与障碍物图标写法一致，但仅两类：start / end）
    Object.entries(lineIconPaths).forEach(([key, path]) => {
      const tex = path ? new THREE.TextureLoader().load(path) : null;
      const materialOpts: any = {
        size: 50,
        sizeAttenuation: false,
        alphaTest: 0.5,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      };
      if (tex) materialOpts.map = tex;

      const material = new THREE.PointsMaterial(materialOpts);

      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array(LINE_ICON_MAX * 3), 3)
      );
      geom.setDrawRange(0, 0);

      const points = new THREE.Points(geom, material);
      (points as any).userData = { count: 0, max: LINE_ICON_MAX };
      this.scene.add(points);
      this.lineIconPool.set(key, points);
    });

    // 3.2 初始化红绿灯图标池（main / grey / pass）
    Object.entries(trafficLightIconPaths).forEach(([key, path]) => {
      const tex = path ? new THREE.TextureLoader().load(path) : null;
      const materialOpts: any = {
        // 红绿灯图标大小
        size: 40,
        sizeAttenuation: false,
        alphaTest: 0.5,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      };
      if (tex) materialOpts.map = tex;

      const material = new THREE.PointsMaterial(materialOpts);

      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        'position',
        new THREE.BufferAttribute(
          new Float32Array(TRAFFIC_LIGHT_ICON_MAX * 3),
          3
        )
      );
      geom.setDrawRange(0, 0);

      const points = new THREE.Points(geom, material);
      (points as any).userData = { count: 0, max: TRAFFIC_LIGHT_ICON_MAX };
      this.scene.add(points);
      this.trafficLightIconPool.set(key, points);
    });

    // 3.3 初始化红绿灯 id 文本池
    this.trafficLightLabelPool = new TrafficLightLabelPool(this.scene);



    // 4. 基础线条系统 (合并渲染 Lanes, Planning 等)
    // 4.1. 窄线样式池
    Object.entries(NARROW_LINE_CONFIGS).forEach(([key, cfg]) => {
      // 特殊处理：如果是障碍物轨迹线，保留顶点色
      const isMultiColor = key === 'obstacleTrajectory';

      let mat: THREE.LineDashedMaterial | THREE.LineBasicMaterial;
      if (cfg.dashed) {
        mat = new THREE.LineDashedMaterial({
          vertexColors: isMultiColor, // 只有它开启
          color: isMultiColor ? 0xffffff : new THREE.Color(...(cfg.rgb || [])),
          dashSize: cfg.dashSize || 1,
          gapSize: cfg.gapSize || 1,
        });
      } else {
        mat = new THREE.LineBasicMaterial({
          vertexColors: isMultiColor,
          color: isMultiColor ? 0xffffff : new THREE.Color(...(cfg.rgb || [])),
        });
      }

      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array(50000 * 3), 3)
      );

      if (isMultiColor) {
        // 只有障碍物轨迹线分配颜色缓冲
        geom.setAttribute(
          'color',
          new THREE.BufferAttribute(new Float32Array(50000 * 3), 3)
        );
      }

      const line = new THREE.LineSegments(geom, mat);
      // 关闭引擎剔除，直接交由 GPU 渲染管线切除视口外顶点
      line.frustumCulled = false;
      this.narrowLineMeshPool.set(key, line);
      this.scene.add(line);
    });

    // 4.2. 宽线系统 (按样式单独 Mesh)：根据配置动态创建
    const wideAlloc = 30000;
    Object.entries(WIDE_LINE_MAT_CONFIGS).forEach(([key, cfg]) => {
      const color = new THREE.Color(...cfg.rgb); // 从配置获取颜色
      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array(wideAlloc * 3), 3)
      );
      const mat = new THREE.MeshBasicMaterial({
        color,
        side: THREE.DoubleSide,
        transparent: !!cfg.transparent,
        opacity: cfg.opacity,
      });
      if (cfg.transparent) mat.depthWrite = false;
      const mesh = new THREE.Mesh(geom, mat);
      mesh.visible = false;
      // 关闭引擎剔除，直接交由 GPU 渲染管线切除视口外顶点
      mesh.frustumCulled = false;
      this.scene.add(mesh);
      this.wideLineMeshPool.set(key, mesh);
    });

    // 5. 点云系统
    const noiseGeom = new THREE.BufferGeometry();
    noiseGeom.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(15000 * 3), 3)
    );
    noiseGeom.setAttribute(
      'color',
      new THREE.BufferAttribute(new Float32Array(15000 * 3), 3)
    );
    this.meshPool.noisePoints = new THREE.Points(
      noiseGeom,
      new THREE.PointsMaterial({ size: 0.15, vertexColors: true })
    );

    // 全部加入场景
    Object.values(this.meshPool).forEach((obj: any) => {
      if (obj instanceof THREE.Object3D) {
        // 关闭引擎剔除，直接交由 GPU 渲染管线切除视口外顶点
        obj.frustumCulled = false;
        this.scene.add(obj);
      }
    });
  }

  /**
   * 主入口：接收并分发渲染数据
   */
  processPackedFrame(data: PackedFrame) {
    try {
      const { meta, bufs } = data;

      // 0. 把所有的mesh平移到以自车为原点的位置
      try {
        const origin = meta?.carInfo?.origin;
        if (
          origin &&
          typeof origin.x === 'number' &&
          typeof origin.y === 'number'
        ) {
          const ox = origin.x || 0;
          const oy = origin.y || 0;
          if (this.meshPool.obsLine)
            this.meshPool.obsLine.position.set(ox, oy, 0);
          if (this.meshPool.obsSurface)
            this.meshPool.obsSurface.position.set(ox, oy, 0);
          if (this.meshPool.globalLines)
            this.meshPool.globalLines.position.set(ox, oy, 0);
          if (this.meshPool.noisePoints)
            this.meshPool.noisePoints.position.set(ox, oy, 0);
          this.iconPool.forEach((p) => p.position.set(ox, oy, 0));
          this.lineIconPool.forEach((p) => p.position.set(ox, oy, 0));
          this.wideLineMeshPool.forEach((m) => m.position.set(ox, oy, 0));
          this.narrowLineMeshPool.forEach((line) =>
            line.position.set(ox, oy, 0)
          );
          this.trafficLightIconPool.forEach((p) => p.position.set(ox, oy, 0));
          this.trafficLightLabelPool.setOrigin(ox, oy);
        }
      } catch (_) {}

      // 1. 更新线框 (位置 + 颜色)
      this.updateAttribute(this.meshPool.obsLine, 'position', bufs.obsLine);
      this.updateAttribute(this.meshPool.obsLine, 'color', bufs.obsLineCol);
      (this.meshPool.obsLine.geometry as THREE.BufferGeometry).setDrawRange(
        0,
        new Float32Array(bufs.obsLine).length / 3
      );

      // 2. 更新填充面 (位置 + 颜色 + 索引)
      this.updateAttribute(
        this.meshPool.obsSurface,
        'position',
        bufs.obsSurface
      );
      this.updateAttribute(this.meshPool.obsSurface, 'color', bufs.obsSurfCol);
      const indexArray = new Uint32Array(bufs.obsIndex);
      (this.meshPool.obsSurface.geometry as THREE.BufferGeometry).setIndex(
        new THREE.Uint32BufferAttribute(indexArray, 1)
      );
      // 对于索引化的几何体，setDrawRange 的 count 应为索引数量（元素数量），
      // 而不是顶点数量。错误的 count 会导致面不渲染或渲染异常。
      (this.meshPool.obsSurface.geometry as THREE.BufferGeometry).setDrawRange(
        0,
        indexArray.length
      );

      // 3. 更新图标 (按类型分发) — 使用 Points 缓冲写入位置
      const allIconData = new Float32Array(bufs.obsAllIcons);
      const obstacleIconRanges = meta.obstacleIconRanges || {};

      // 3.1 遍历 iconPool
      this.iconPool.forEach((points, type) => {
        const range = obstacleIconRanges[type];
        const geom = points.geometry as THREE.BufferGeometry;
        if (range && range.count > 0) {
          const attr = geom.attributes.position as THREE.BufferAttribute;

          // 使用 subarray 实现“零拷贝”切片：高效截取大 Buffer 中的一段
          // 参数：range.start * 3 是起始浮点数索引，(range.start + range.count) * 3 是结束索引
          const subData = allIconData.subarray(
            range.start * 3,
            (range.start + range.count) * 3
          );

          (attr.array as Float32Array).set(subData);
          attr.needsUpdate = true;
          (attr as any).updateRange = { offset: 0, count: subData.length };

          geom.setDrawRange(0, range.count);
          points.visible = true;
        } else {
          // 没有任何逻辑判断，只要 Range 里没有，就直接关掉
          geom.setDrawRange(0, 0);
          points.visible = false;
        }
      });

      // 3.2 更新 起/终点 图标（如果 worker 提供了 startEndIcons 和 meta.startEndRanges）
      if (bufs.startEndIcons && meta.startEndRanges) {
        const allLineIconData = new Float32Array(bufs.startEndIcons);
        const startEndRanges = meta.startEndRanges || {};

        this.lineIconPool.forEach((points, key) => {
          const range = startEndRanges[key];
          const geom = points.geometry as THREE.BufferGeometry;
          if (range && range.count > 0) {
            const attr = geom.attributes.position as THREE.BufferAttribute;

            const subData = allLineIconData.subarray(
              range.start * 3,
              (range.start + range.count) * 3
            );

            (attr.array as Float32Array).set(subData);
            attr.needsUpdate = true;
            attr.updateRange = { offset: 0, count: subData.length };

            geom.setDrawRange(0, range.count);

            points.visible = true;
          } else {
            geom.setDrawRange(0, 0);
            points.visible = false;
          }
        });
      } else {
        // 如果没有数据则全部隐藏
        this.lineIconPool.forEach((p) => {
          (p.geometry as THREE.BufferGeometry).setDrawRange(0, 0);
          p.visible = false;
        });
      }

      // 3.3 更新 红绿灯信息
      // （1）红绿灯图标（按 key: main / grey / pass 分发）
      if (bufs.trafficLightAllIcons && meta.trafficLightIconRanges) {
        const allTrafficLightData = new Float32Array(bufs.trafficLightAllIcons);
        const trafficLightRanges = meta.trafficLightIconRanges || {};

        this.trafficLightIconPool.forEach((points, key) => {
          const range = trafficLightRanges[key];
          const geom = points.geometry as THREE.BufferGeometry;

          if (range && range.count > 0) {
            const attr = geom.attributes.position as THREE.BufferAttribute;
            const subData = allTrafficLightData.subarray(
              range.start * 3,
              (range.start + range.count) * 3
            );

            (attr.array as Float32Array).set(subData);
            attr.needsUpdate = true;
            attr.updateRange = { offset: 0, count: subData.length };

            geom.setDrawRange(0, range.count);
            points.visible = true;
          } else {
            geom.setDrawRange(0, 0);
            points.visible = false;
          }
        });
      } else {
        this.trafficLightIconPool.forEach((p) => {
          (p.geometry as THREE.BufferGeometry).setDrawRange(0, 0);
          p.visible = false;
        });
      }
      // （2）红绿灯 id 文本
      try {
        const lights = (meta && meta.trafficLight) || [];
        this.frameCounter = (this.frameCounter || 0) + 1;

        // 更新文本
        this.trafficLightLabelPool.update(lights, this.frameCounter);
      } catch (_) {}



      // 4. 更新合并线条 (Lanes, Planning, etc.)
      // 4.1. 更新窄线
      const narrowLinePos = bufs.narrowLinePos
        ? new Float32Array(bufs.narrowLinePos)
        : null;
      const narrowLineCol = bufs.narrowLineCol
        ? new Float32Array(bufs.narrowLineCol)
        : null;
      this.narrowLineMeshPool.forEach((line, key) => {
        const range = meta.narrowLineRanges[key];
        if (range && range.count > 0 && narrowLinePos) {
          this.updateAttrDirect(line, range, narrowLinePos, narrowLineCol);

          // 只有虚线材质需要计算距离，且只在当前 Line 有点时计算
          if ((line.material as any).isLineDashedMaterial) {
            line.computeLineDistances();
          }
          line.visible = true;
        } else {
          line.visible = false;
        }
      });

      // 4.2. 更新宽线（使用合并的 wideLinePos / wideLineCol + meta.wideLineRanges）
      try {
        const widePos = bufs.wideLinePos
          ? new Float32Array(bufs.wideLinePos)
          : null;
        const wideCol = bufs.wideLineCol
          ? new Float32Array(bufs.wideLineCol)
          : null;
        const wideRanges = meta.wideLineRanges || {};

        this.wideLineMeshPool.forEach((mesh, key) => {
          const range = wideRanges[key];
          if (range && range.count > 0 && widePos) {
            this.updateAttrDirect(mesh, range, widePos, wideCol);
            mesh.visible = true;
          } else {
            (mesh.geometry as THREE.BufferGeometry).setDrawRange(0, 0);
            mesh.visible = false;
          }
        });
      } catch (err) {
        console.warn('wideLine update failed', err);
      }

      // 5. 更新点云
      this.updateAttribute(
        this.meshPool.noisePoints,
        'position',
        bufs.noisePos
      );
      this.updateAttribute(this.meshPool.noisePoints, 'color', bufs.noiseCol);
      (this.meshPool.noisePoints.geometry as THREE.BufferGeometry).setDrawRange(
        0,
        meta.noisePointsCount
      );
    } catch (err) {
      console.error('MeshPool.processPackedFrame error', err);
      return;
    }
  }

  /**
   * 更新geom属性
   *
   * @private
   * @param {(THREE.LineSegments | THREE.Mesh | THREE.Points)} mesh
   * @param {string} attrName
   * @param {ArrayBuffer} buffer
   * @memberof MeshPool
   */
  private updateAttribute(
    mesh: THREE.LineSegments | THREE.Mesh | THREE.Points,
    attrName: string,
    buffer: ArrayBuffer
  ) {
    try {
      const geom = mesh.geometry as THREE.BufferGeometry;
      const attr = geom.attributes[attrName];
      const data = new Float32Array(buffer);

      if (data.length > attr.array.length) {
        // 自动扩容
        geom.setAttribute(attrName, new THREE.BufferAttribute(data, 3));
      } else {
        (attr.array as Float32Array).set(data);
        attr.needsUpdate = true;
      }
    } catch (err) {
      console.warn('updateAttribute failed', attrName, err);
    }
  }

  /**
   * 更新geom属性和颜色
   *
   * @private
   * @param {*} mesh
   * @param {*} range
   * @param {Float32Array} posData
   * @param {(Float32Array | null)} [colData]
   * @memberof MeshPool
   */
  private updateAttrDirect(
    mesh: any,
    range: any,
    posData: Float32Array,
    colData?: Float32Array | null
  ) {
    try {
      const geom = mesh.geometry;
      const pAttr = geom.attributes.position;
      const cAttr = geom.attributes.color; // 这里的 cAttr 在纯色模式下是 undefined

      const startIdx = range.start * 3;
      const endIdx = (range.start + range.count) * 3;
      const subPos = posData.subarray(startIdx, endIdx);

      // 1. 更新位置
      if (subPos.length > pAttr.array.length) {
        geom.setAttribute(
          'position',
          new THREE.BufferAttribute(new Float32Array(subPos), 3)
        );
      } else {
        pAttr.array.set(subPos);
        pAttr.needsUpdate = true;
        pAttr.updateRange = { offset: 0, count: subPos.length };
      }

      // 2. 只有存在顶点色属性时才更新颜色
      if (cAttr && colData && colData.length > 0) {
        const subCol = colData.subarray(startIdx, endIdx);
        if (subCol.length > cAttr.array.length) {
          geom.setAttribute(
            'color',
            new THREE.BufferAttribute(new Float32Array(subCol), 3)
          );
        } else {
          cAttr.array.set(subCol);
          cAttr.needsUpdate = true;
          cAttr.updateRange = { offset: 0, count: subCol.length };
        }
      }

      geom.setDrawRange(0, range.count);
    } catch (err) {
      console.warn('updateAttrDirect failed', err);
    }
  }

  /**
   * 屏幕空间布局：检测可见的红绿灯标签，在屏幕上避免重叠（简单分组堆叠策略）。
   * 设计：每帧使用各标签的锚点世界坐标投影到屏幕，发现相互靠近的簇后沿屏幕 Y 方向堆叠，
   * 再将目标屏幕位置反投影回世界坐标并写回 sprite.position（相对于 labelGroup）。
   */
  public layoutTrafficLightLabels(
    camera: THREE.Camera | null,
    renderer?: { domElement: { clientWidth: number; clientHeight: number } }
  ) {
    this.trafficLightLabelPool.layout(camera, renderer);
  }

  /**
   * 释放mesh
   *
   * @private
   * @param {THREE.Object3D} mesh
   * @memberof MeshPool
   */
  private disposeMesh = (mesh: THREE.Object3D) => {
    this.scene.remove(mesh);
    const geom = (mesh as any).geometry;
    if (geom && typeof geom.dispose === 'function') geom.dispose();
    const mat = (mesh as any).material;
    if (mat) {
      if (Array.isArray(mat)) {
        mat.forEach((m: any) => {
          if (m.map && typeof m.map.dispose === 'function') m.map.dispose();
          if (typeof m.dispose === 'function') m.dispose();
        });
      } else {
        if (mat.map && typeof mat.map.dispose === 'function') mat.map.dispose();
        if (typeof mat.dispose === 'function') mat.dispose();
      }
    }
  };

  /**
   * 释放所有 mesh/geometry/material/texture 资源
   */
  dispose() {
    // 1. meshPool 内对象
    Object.values(this.meshPool).forEach((obj: any) => {
      if (obj instanceof THREE.Object3D) this.disposeMesh(obj);
    });

    // 2. 图标池
    this.iconPool.forEach((points) => this.disposeMesh(points));
    this.lineIconPool.forEach((points) => this.disposeMesh(points));
    this.trafficLightIconPool.forEach((points) => this.disposeMesh(points));

    // 3. 线条池
    this.narrowLineMeshPool.forEach((line) => this.disposeMesh(line));
    this.wideLineMeshPool.forEach((mesh) => this.disposeMesh(mesh));

    // 4. 红绿灯标签池
    if (
      this.trafficLightLabelPool &&
      typeof this.trafficLightLabelPool.dispose === 'function'
    ) {
      this.trafficLightLabelPool.dispose();
    }



    // 清空引用
    this.meshPool = {} as MeshPoolObjects;
    this.iconPool.clear();
    this.lineIconPool.clear();
    this.trafficLightIconPool.clear();
    this.narrowLineMeshPool.clear();
    this.wideLineMeshPool.clear();
  }
}
